
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Edit2, Trash2, MessageSquare } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EmojiPicker from "./EmojiPicker";
import { isValidEmoji, processEmoji } from "@/utils/emojiUtils";

interface MessageActionsProps {
  messageId: string;
  onEdit: () => void;
  onDelete: () => void;
  onReply?: () => void;
  onAddSticker?: (messageId: string, emoji: string) => void;
  disabled?: boolean;
  showReplyAndSticker?: boolean;
  isCurrentUser?: boolean;
  canDeleteOthersMessages?: boolean;
}

export default function MessageActions({ 
  messageId, 
  onEdit, 
  onDelete, 
  onReply,
  onAddSticker,
  disabled,
  showReplyAndSticker = false,
  isCurrentUser = false,
  canDeleteOthersMessages = false
}: MessageActionsProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDelete = () => {
    onDelete();
    setShowDeleteDialog(false);
  };

  const handleEmojiSelect = (emoji: string) => {
    console.log("MessageActions - Emoji selected:", { messageId, emoji });
    
    // Enhanced validation to prevent invalid emojis
    if (!messageId || !emoji) {
      console.error("MessageActions - Invalid parameters:", { messageId, emoji });
      return;
    }

    // Additional safety check to prevent messageId/emoji confusion
    if (messageId === emoji) {
      console.error("MessageActions - MessageId and emoji are identical, preventing corruption");
      return;
    }

    // Validate the emoji before processing
    if (!isValidEmoji(emoji)) {
      console.error("MessageActions - Invalid emoji selected:", emoji);
      return;
    }

    // Process the emoji to ensure proper normalization
    const processedEmoji = processEmoji(emoji, 'MessageActions selection');
    
    // Don't add fallback emojis
    if (processedEmoji === '❓') {
      console.error("MessageActions - Processed emoji resulted in fallback, not adding");
      return;
    }

    if (onAddSticker) {
      console.log("MessageActions - Adding processed emoji:", { original: emoji, processed: processedEmoji });
      onAddSticker(messageId, processedEmoji);
    }
  };

  if (disabled) return null;

  return (
    <div className="flex items-center gap-1">
      {/* Reply Button */}
      {showReplyAndSticker && onReply && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 w-6 p-0 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm"
          onClick={onReply}
        >
          <MessageSquare className="h-3 w-3" />
        </Button>
      )}

      {/* Sticker Button with EmojiPicker */}
      {showReplyAndSticker && onAddSticker && (
        <EmojiPicker 
          onEmojiSelect={handleEmojiSelect}
        />
      )}

      {/* Direct Delete Button for privileged users on others' messages */}
      {!isCurrentUser && canDeleteOthersMessages && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 w-6 p-0 rounded-full bg-red-500/20 hover:bg-red-500/40 backdrop-blur-sm"
          onClick={() => setShowDeleteDialog(true)}
        >
          <Trash2 className="h-3 w-3 text-red-400" />
        </Button>
      )}

      {/* More Actions Dropdown - Only for own messages */}
      {isCurrentUser && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm"
            >
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={onEdit} className="cursor-pointer">
              <Edit2 className="h-3 w-3 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setShowDeleteDialog(true)} 
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3 w-3 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
